import React, { useMemo, useState } from "react";
import {
  trainingGuides,
  trainingMediaSrc,
  trainingSections,
} from "../../data/trainingGuides";
import { useTrainingGuide } from "../../components/AdminTrainingGuide";
import "../../assets/css/AdminTrainingGuide.css";

const AdminTraining = () => {
  const training = useTrainingGuide();
  const [query, setQuery] = useState("");

  const sections = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const byId = Object.fromEntries(trainingGuides.map((guide) => [guide.id, guide]));
    return trainingSections
      .map((section) => {
        const guides = section.guideIds
          .map((id) => byId[id])
          .filter(Boolean)
          .filter((guide) => {
            if (!needle) return true;
            return [guide.title, guide.description, guide.topic, section.title]
              .join(" ")
              .toLowerCase()
              .includes(needle);
          });
        return { ...section, guides };
      })
      .filter((section) => section.guides.length > 0);
  }, [query]);

  const total = sections.reduce((sum, section) => sum + section.guides.length, 0);

  const jumpTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="at-page">
      <header className="at-hero">
        <div className="at-hero-copy">
          <p className="at-kicker">LoveStrong Training Videos</p>
          <div className="at-title-row">
            <h1>Admin Training Center</h1>
            <span className="at-count">{total}</span>
          </div>
          <p className="at-lead">
            Watch a Guide Videos
          </p>
        </div>
        <div className="at-toolbar">
          <label className="at-search">
            <i className="mdi mdi-magnify" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Training Videos"
              aria-label="Search Training Videos"
            />
          </label>
          {sections.length > 1 ? (
            <nav className="at-jump" aria-label="Training sections">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className="at-jump-chip"
                  onClick={() => jumpTo(`training-${section.id}`)}
                >
                  <i className={`mdi ${section.icon}`} aria-hidden="true" />
                  {section.title}
                  <span>{section.guides.length}</span>
                </button>
              ))}
            </nav>
          ) : null}
        </div>
      </header>

      {sections.length === 0 ? (
        <p className="at-empty">No guides match that search.</p>
      ) : (
        <div className="at-sections">
          {sections.map((section) => (
            <section
              key={section.id}
              id={`training-${section.id}`}
              className="at-section"
            >
              <div className="at-section-head">
                <span className="at-section-icon" aria-hidden="true">
                  <i className={`mdi ${section.icon}`} />
                </span>
                <div>
                  <h2>{section.title}</h2>
                </div>
                <span className="at-section-count">
                  {section.guides.length}{" "}
                  {section.guides.length === 1 ? "video" : "videos"}
                </span>
              </div>
              <div className="at-list">
                {section.guides.map((guide, index) => (
                  <GuideRow
                    key={guide.id}
                    guide={guide}
                    index={index}
                    onOpen={() => training?.openGuide(guide.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const GuideRow = ({ guide, index, onOpen }) => (
  <button
    type="button"
    className="at-row"
    style={{ "--at-i": index }}
    onClick={onOpen}
  >
    <span className="at-row-num" aria-hidden="true">
      {String(index + 1).padStart(2, "0")}
    </span>
    <span className={`at-thumb${guide.thumbnail ? " has-image" : ""}`}>
      {guide.thumbnail ? (
        <img src={trainingMediaSrc(guide.thumbnail)} alt="" />
      ) : null}
      <span className="at-play" aria-hidden="true">
        <i className="mdi mdi-play" />
      </span>
      <span className="at-length">{guide.lengthLabel}</span>
    </span>
    <span className="at-card-copy">
      <span className="at-topic">{guide.topic}</span>
      <h3>{guide.title}</h3>
      <p>{guide.description}</p>
      <span className="at-row-cta">
        Watch
        <i className="mdi mdi-arrow-right" aria-hidden="true" />
      </span>
    </span>
  </button>
);

export default AdminTraining;
